import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent, type ReactNode, type PointerEvent as ReactPointerEvent } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../../../design-system/icons';
import type { Box } from './geometry';
import './dnd.css';

/**
 * Calque d'interaction du canevas des studios (ADR-146 §5.3, addendum (13)) : contours de survol et de
 * sélection avec l'étiquette du bloc en français et ses poignées, barre d'outils flottante au-dessus de
 * l'élément choisi, ligne d'insertion et refus de la recette pendant un glisser, zones de dépôt, éditeur
 * de libellé sur place. Le calque est à l'échelle de l'écran (pas réduit avec l'aperçu, comme Figma ou
 * Webflow) et ne connaît aucun schéma : il ne reçoit que des boîtes, des libellés et des actions.
 */

export interface FrameSpec {
  key: string;
  box: Box;
  tone: 'hover' | 'selected' | 'multi';
  /** Étiquette (« ❖ Chasseur · Bloc ») ; jamais un identifiant. */
  label?: string;
  /**
   * Poignées réelles de redimensionnement (largeur sur la grille de 12 colonnes, ADR-146 addendum (14)) ; sans
   * elles, le contour n'a pas de poignées : une poignée décorative laisserait croire à un geste qui n'existe pas.
   */
  resize?: { onStart: (side: 'e' | 'w', event: ReactPointerEvent<HTMLSpanElement>) => void } | undefined;
}

export interface ToolbarAction {
  id: string;
  label: string;
  /** Nom de l'icône dans le registre du design system. */
  icon: string;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
  keys?: string;
}

export interface MenuAction {
  id: string;
  label: string;
  onClick: () => void;
  disabled?: boolean;
}

export interface DropZone {
  key: string;
  box: Box;
  label: string;
  /** Zone qui reçoit le dépôt survolé. */
  active?: boolean;
}

/** Cible de dépôt qui n'a pas de place visible dans l'aperçu (région vide, autre page). */
export interface DockTarget {
  target: string;
  label: string;
  active?: boolean;
  refused?: boolean;
}

export interface InlineEdit {
  /** Identité de l'édition (un autre libellé remonte l'éditeur). */
  id: string;
  box: Box;
  value: string;
  label: string;
  fontSize?: number;
  onCommit: (value: string) => void;
  onCancel: () => void;
}

/** Icônes de la barre flottante : noms du registre du design system (mêmes icônes que la coquille). */
export const ICONS = {
  parent: 'corner-up',
  up: 'arrow-up',
  down: 'arrow-down',
  left: 'back',
  right: 'chevron',
  duplicate: 'copy',
  remove: 'trash',
  move: 'move',
  more: 'more',
  rename: 'pencil',
  add: 'plus',
} as const;

const style = (box: Box) => ({ left: box.left, top: box.top, width: box.width, height: box.height });

/**
 * Contour d'un élément. Une poignée qui ne redimensionne rien n'est pas montrée (ADR-146 addendum (15)) ;
 * seules les poignées de largeur d'un champ (12 colonnes, contrat 1.10.0) le sont, et elles agissent
 * (addendum (18)).
 */
function Frame({ frame }: { frame: FrameSpec }) {
  return (
    <div className={`ay-dnd-frame ay-dnd-frame--${frame.tone}`} style={style(frame.box)} aria-hidden="true">
      {frame.label !== undefined && <span className="ay-dnd-tag">{frame.label}</span>}
      {frame.tone === 'selected' && frame.resize !== undefined && (['w', 'e'] as const).map((at) => (
        <span key={at} className={`ay-dnd-handle ay-dnd-handle--${at} is-live`} role="presentation" title="Glisser pour changer la largeur" onPointerDown={(event) => frame.resize!.onStart(at, event)} />
      ))}
    </div>
  );
}

const TOOLBAR_HEIGHT = 40;
/** Hauteur de l'étiquette posée au-dessus du contour, marge comprise. */
const TAG_HEIGHT = 26;

/**
 * Place de la barre flottante (ADR-146 addendum (15)) : jamais sur le contenu de l'aperçu. Dans la marge de la
 * scène au-dessus de l'appareil, sinon sous l'appareil ; sans appareil visible en entier (zoom), au-dessus de
 * l'étiquette de l'élément, sinon dessous, sinon dans son coin. Alignée à droite de l'élément.
 */
export function toolbarPlace(anchor: Box, width: number, bounds: Box | undefined, screen: Box | undefined, viewport: { width: number; height: number }): { top: number; left: number } {
  const ceiling = (bounds?.top ?? 0) + 4;
  const floor = (bounds === undefined ? viewport.height : bounds.top + bounds.height) - 4;
  let top: number | undefined;
  if (screen !== undefined) {
    const above = screen.top - TOOLBAR_HEIGHT - 6;
    const below = screen.top + screen.height + 6;
    if (above >= ceiling) top = above;
    else if (below + TOOLBAR_HEIGHT <= floor) top = below;
  }
  if (top === undefined) {
    const aboveTag = anchor.top - TAG_HEIGHT - TOOLBAR_HEIGHT - 4;
    const below = anchor.top + anchor.height + 8;
    top = aboveTag >= ceiling ? aboveTag : below + TOOLBAR_HEIGHT <= floor ? below : Math.max(ceiling, anchor.top + 6);
  }
  const right = bounds === undefined ? viewport.width : bounds.left + bounds.width;
  const left = Math.max((bounds?.left ?? 0) + 4, Math.min(right - width - 4, anchor.left + anchor.width - width));
  return { top, left };
}

/** Barre flottante : hors du contenu de l'aperçu (voir {@link toolbarPlace}). */
function Toolbar({ anchor, name, actions, menu, bounds, screen }: { anchor: Box; name: string; actions: readonly ToolbarAction[]; menu: readonly MenuAction[]; bounds?: Box | undefined; screen?: Box | undefined }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const measured = ref.current?.getBoundingClientRect().width ?? 0;
    if (Math.abs(measured - width) > 0.5) setWidth(measured);
  });
  useEffect(() => setOpen(false), [name]);
  const { top, left } = toolbarPlace(anchor, width, bounds, screen, { width: typeof window === 'undefined' ? 1280 : window.innerWidth, height: typeof window === 'undefined' ? 800 : window.innerHeight });
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Escape' && open) {
      event.stopPropagation();
      setOpen(false);
    }
  };
  return (
    <div ref={ref} className="ay-dnd-toolbar" role="toolbar" aria-label={`Élément choisi : ${name}`} style={{ top, left }} onKeyDown={onKeyDown}>
      {actions.map((action) => {
        const name = `${action.label}${action.keys === undefined ? '' : ` · ${action.keys}`}`;
        return (
          <button key={action.id} type="button" className={`ay-dnd-toolbar__button${action.danger === true ? ' is-danger' : ''}`} disabled={action.disabled} aria-label={name} title={name} {...(action.keys === undefined ? {} : { 'aria-keyshortcuts': action.keys })} onClick={action.onClick}>
            <Icon name={action.icon} size={16} />
          </button>
        );
      })}
      {menu.length > 0 && (
        <span className="ay-dnd-toolbar__more">
          <button type="button" className="ay-dnd-toolbar__button" aria-haspopup="menu" aria-expanded={open} aria-label="Plus d’actions" title="Plus d’actions" onClick={() => setOpen((value) => !value)}>
            <Icon name="more" size={16} />
          </button>
          {open && (
            <span role="menu" className="ay-dnd-menu" aria-label={`Plus d’actions sur « ${name} »`}>
              {menu.map((item, index) => (
                <button key={item.id} type="button" role="menuitem" autoFocus={index === 0} disabled={item.disabled} onClick={() => { setOpen(false); item.onClick(); }}>{item.label}</button>
              ))}
            </span>
          )}
        </span>
      )}
    </div>
  );
}

function Editor({ edit }: { edit: InlineEdit }) {
  const [value, setValue] = useState(edit.value);
  const done = useRef(false);
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    ref.current?.focus();
    ref.current?.select();
  }, []);
  const commit = (): void => {
    if (done.current) return;
    done.current = true;
    edit.onCommit(value);
  };
  return (
    <input
      ref={ref}
      className="ay-dnd-inline"
      aria-label={edit.label}
      value={value}
      style={{ ...style({ ...edit.box, width: Math.max(edit.box.width + 16, 120), height: Math.max(edit.box.height + 6, 28) }), left: edit.box.left - 8, top: edit.box.top - 3, ...(edit.fontSize === undefined ? {} : { fontSize: edit.fontSize }) }}
      onChange={(event) => setValue(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        // L'édition garde ses touches : rien ne part vers les raccourcis de l'éditeur.
        event.stopPropagation();
        if (event.key === 'Enter') {
          event.preventDefault();
          commit();
        } else if (event.key === 'Escape') {
          event.preventDefault();
          done.current = true;
          edit.onCancel();
        }
      }}
    />
  );
}

/** Découpe du calque à la scène du canevas : un contour ne déborde jamais sur les panneaux. */
function clipOf(box: Box | undefined): string | undefined {
  if (box === undefined || typeof window === 'undefined') return undefined;
  const right = Math.max(0, window.innerWidth - (box.left + box.width));
  const bottom = Math.max(0, window.innerHeight - (box.top + box.height));
  return `inset(${Math.max(0, box.top)}px ${right}px ${bottom}px ${Math.max(0, box.left)}px)`;
}

export function InteractionLayer({ root, frames, toolbar, line, zones, dock, ghost, edit, live, extra, clip, screen }: {
  root: Element | null;
  frames: readonly FrameSpec[];
  toolbar?: { anchor: Box; name: string; actions: readonly ToolbarAction[]; menu: readonly MenuAction[] } | undefined;
  line?: { box: Box; refused?: string | null } | undefined;
  zones?: readonly DropZone[];
  dock?: { title: string; targets: readonly DockTarget[] } | undefined;
  ghost?: { x: number; y: number; label: string; refused?: boolean } | undefined;
  edit?: InlineEdit | undefined;
  live: string;
  extra?: ReactNode;
  /** Scène du canevas : la barre flottante y reste. */
  clip?: Box | undefined;
  /** Écran de l'appareil d'aperçu : contours et zones n'en débordent pas (ce qui défile hors de l'écran est coupé). */
  screen?: Box | undefined;
}) {
  if (root === null) return null;
  const clipPath = clipOf(screen ?? clip);
  return createPortal(
    <div className="ay-dnd-layer">
      <div className="ay-dnd-clip" style={clipPath === undefined ? undefined : { clipPath }}>
      {(zones ?? []).map((zone) => (
        <div key={zone.key} className={`ay-dnd-zone${zone.active === true ? ' is-active' : ''}`} style={style(zone.box)} aria-hidden="true">
          <span className="ay-dnd-zone__label">{zone.label}</span>
        </div>
      ))}
      {frames.map((frame) => <Frame key={frame.key} frame={frame} />)}
      </div>
      {toolbar !== undefined && <Toolbar anchor={toolbar.anchor} name={toolbar.name} actions={toolbar.actions} menu={toolbar.menu} bounds={clip} screen={screen} />}
      {line !== undefined && (
        <div className={`ay-dnd-line${line.refused === null || line.refused === undefined ? '' : ' is-refused'}`} style={style(line.box)} aria-hidden="true">
          {line.refused !== null && line.refused !== undefined && <span className="ay-dnd-refusal">{line.refused}</span>}
        </div>
      )}
      {dock !== undefined && dock.targets.length > 0 && (
        <div className="ay-dnd-dock" aria-hidden="true" style={clip === undefined ? undefined : { left: clip.left + clip.width / 2, top: clip.top + clip.height - 70, bottom: 'auto' }}>
          <span className="ay-dnd-dock__title">{dock.title}</span>
          {dock.targets.map((target) => (
            <span key={target.target} data-drop-target={target.target} className={`ay-dnd-dock__target${target.active === true ? ' is-active' : ''}${target.refused === true ? ' is-refused' : ''}`}>{target.label}</span>
          ))}
        </div>
      )}
      {ghost !== undefined && (
        <div className={`ay-dnd-ghost${ghost.refused === true ? ' is-refused' : ''}`} style={{ left: ghost.x + 14, top: ghost.y + 14 }} aria-hidden="true">{ghost.label}</div>
      )}
      {edit !== undefined && <Editor key={edit.id} edit={edit} />}
      {extra}
      <div className="ay-visually-hidden" aria-live="assertive" aria-atomic="true" data-testid="canvas-live">{live}</div>
    </div>,
    root,
  );
}
